import {PlayArrow, Quiz} from '@mui/icons-material';
import {Button, Card, CardActions, CardContent, Typography, Stack} from '@mui/material';
import {useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {ExamModule, ModuleTopic, Progress} from '@cp/model';
import CourseCategoryTopicCardHeader from './CourseCategoryTopicCardHeader';

export interface CourseCategoryTopicCardProps {
  topic: ModuleTopic;
  subjectColor: {bg: string; border: string; icon: string};
  progressScore?: number;
  progress?: Progress;
  onCreatePracticeSession?: (topic: string, difficulty: number) => Promise<void>;
  examModule: ExamModule;
}

export default function CourseCategoryTopicCard({
  topic,
  subjectColor,
  progressScore,
  progress,
  onCreatePracticeSession,
  examModule,
}: CourseCategoryTopicCardProps) {
  const navigate = useNavigate();
  const {examModuleId} = useParams<{examModuleId: string}>();
  const [showContent, setShowContent] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  const handleStudy = () => {
    if (examModuleId) {
      navigate(`/course/${examModuleId}/study/${topic.topic}`);
    }
  };

  const getCurrentDifficulty = (): number => {
    if (!progress?.scores) return 1;

    // Find all progress scores for this topic
    const topicScores = progress.scores.filter((score) => score.topic === topic.topic);
    if (topicScores.length === 0) return 1;

    // Find the highest difficulty that has been attempted
    const highestDifficulty = Math.max(...topicScores.map((score) => score.difficulty));

    // Check if the highest difficulty has been passed (adjustedAccuracy >= 0.8)
    const highestScore = topicScores.find((score) => score.difficulty === highestDifficulty);
    if (highestScore && highestScore.adjustedAccuracy >= 0.8) {
      // If passed, use next difficulty level (capped at 5)
      return Math.min(highestDifficulty + 1, 5);
    }

    // Otherwise, use the current highest difficulty
    return highestDifficulty;
  };

  const handlePractice = async () => {
    if (!examModuleId || !onCreatePracticeSession) return;

    setIsCreatingSession(true);
    try {
      const difficulty = getCurrentDifficulty();
      await onCreatePracticeSession(topic.topic, difficulty);
    } catch (error) {
      console.error('Failed to create practice session:', error);
    } finally {
      setIsCreatingSession(false);
    }
  };

  const handleToggleContent = () => {
    setShowContent((prev) => !prev);
  };

  return (
    <Card
      sx={{
        boxShadow: 2,
        border: `2px solid ${subjectColor.border}40`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: subjectColor.border,
          boxShadow: 4,
        },
      }}
      data-testid="course-topic-card"
      data-topic={topic.topic}
    >
      <CourseCategoryTopicCardHeader
        topic={topic}
        subjectColor={subjectColor}
        progressScore={progressScore}
        progress={progress}
        showContent={showContent}
        onToggleContent={handleToggleContent}
      />
      {showContent && (
        <CardContent data-testid="course-topic-card-content">
          {topic.detail && (
            <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.875rem'}}>
              {topic.detail}
            </Typography>
          )}
          {!topic.detail && (
            <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.875rem', fontStyle: 'italic'}}>
              No description available
            </Typography>
          )}
        </CardContent>
      )}
      {(!examModule.disableStudy || (onCreatePracticeSession && !examModule.disableQuestion)) && (
        <CardActions
          sx={{
            p: 2,
            pt: 0,
            justifyContent: 'flex-end',
          }}
          data-testid="course-topic-card-actions"
        >
          <Stack direction="row" spacing={1}>
            {!examModule.disableStudy && (
              <Button
                variant="outlined"
                startIcon={<PlayArrow />}
                onClick={handleStudy}
                size="small"
                sx={{
                  borderColor: subjectColor.border,
                  color: subjectColor.border,
                  fontWeight: 600,
                  minWidth: 'auto',
                  px: 2,
                  '&:hover': {
                    borderColor: subjectColor.border,
                    bgcolor: `${subjectColor.border}10`,
                  },
                }}
                data-testid="course-topic-study-button"
              >
                Study
              </Button>
            )}
            {onCreatePracticeSession && !examModule.disableQuestion && (
              <Button
                variant="contained"
                startIcon={<Quiz />}
                onClick={handlePractice}
                disabled={isCreatingSession}
                size="small"
                sx={{
                  bgcolor: subjectColor.border,
                  color: 'white',
                  fontWeight: 600,
                  minWidth: 'auto',
                  px: 2,
                  '&:hover': {
                    bgcolor: subjectColor.border,
                    opacity: 0.9,
                  },
                }}
                data-testid="course-topic-practice-button"
              >
                Practice
              </Button>
            )}
          </Stack>
        </CardActions>
      )}
    </Card>
  );
}
